import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import DataTable from '@/components/admin/DataTable';
import {
  ConfirmDeleteDialog,
  InlineSelect,
  StatusBadge,
  courseLabel,
  formatDate,
  publishStatusOptions,
} from '@/components/admin/AcademicTableHelpers';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { createExam, deleteExam, getCourses, getExams, getMyCourses, getMyExams, updateExam } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ClipboardList, Pencil, Plus, PlusCircle, Trash2, X } from 'lucide-react';

function filterParam(value) {
  return value === 'all' ? undefined : value;
}

// The kinds of question a paper can hold. A question authored before types
// existed carries no `type` and is a four-option multiple choice, so that is
// what an absent value means everywhere it is read.
const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'true_false', label: 'True / False' },
  { value: 'short_answer', label: 'Short answer' },
];

const TRUE_FALSE_OPTIONS = ['True', 'False'];
const MAX_OPTIONS = 6;
const MIN_OPTIONS = 2;

const blankQuestion = () => ({
  text: '',
  type: 'multiple_choice',
  options: ['', '', '', ''],
  correct_answer: 0,
  accepted_answers: [],
  explanation: '',
  marks: 5,
});

/** Fill in the fields older question records predate, so the editor is total. */
const withDefaults = (question) => ({
  explanation: '',
  accepted_answers: [],
  type: 'multiple_choice',
  options: [],
  correct_answer: 0,
  marks: 1,
  ...question,
  type: question.type || 'multiple_choice',
});

/** Switching type has to leave the question answerable in its new shape. */
function retypedQuestion(question, type) {
  if (type === 'true_false') {
    return { ...question, type, options: [...TRUE_FALSE_OPTIONS], correct_answer: question.correct_answer === 1 ? 1 : 0 };
  }
  if (type === 'short_answer') {
    return { ...question, type, options: [], correct_answer: 0 };
  }
  const options = (question.options || []).filter((option) => option !== undefined);
  const restored = options.length >= MIN_OPTIONS ? options : ['', '', '', ''];
  return { ...question, type, options: restored, correct_answer: Math.min(question.correct_answer || 0, restored.length - 1) };
}

export default function ManageExams({ instructorMode = false }) {
  const { loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [savingInline, setSavingInline] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    course_id: '',
    duration_minutes: 60,
    due_date: '',
    status: 'draft',
    questions: [],
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [examRes, courseRes] = await Promise.all([
        instructorMode ? getMyExams() : getExams({ course_id: filterParam(courseFilter), status: filterParam(statusFilter) }),
        instructorMode ? getMyCourses() : getCourses(),
      ]);
      const nextExams = examRes.data || [];
      setExams(nextExams);
      setCourses(courseRes.data || []);
      setSelectedIds((prev) => {
        const visible = new Set(nextExams.map((exam) => exam.id));
        return new Set([...prev].filter((id) => visible.has(id)));
      });
    } catch (err) {
      console.error(err);
      toast.error('Could not load exams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, courseFilter, statusFilter]);

  // ?exam=<id> opens that exam's question editor directly. The course room's
  // "Edit questions" used to land here on the unfiltered list of every exam,
  // with nothing saying which row you came from.
  useEffect(() => {
    const wanted = searchParams.get('exam');
    if (!wanted || !exams.length) return;
    const exam = exams.find((row) => row.id === wanted);
    if (exam) {
      openEdit(exam);
    } else {
      toast.error('That quiz is not in your exam list.');
    }
    // Consume the parameter so closing the dialog does not reopen it.
    const next = new URLSearchParams(searchParams);
    next.delete('exam');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exams, searchParams]);

  const courseMap = useMemo(() => {
    const map = {};
    courses.forEach((course) => { map[course.id] = course; });
    return map;
  }, [courses]);

  const filteredExams = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return exams;
    return exams.filter((exam) => {
      const course = courseMap[exam.course_id];
      return (
        (exam.title || '').toLowerCase().includes(query)
        || courseLabel(course).toLowerCase().includes(query)
      );
    });
  }, [courseMap, exams, search]);

  const selectedExams = useMemo(
    () => exams.filter((exam) => selectedIds.has(exam.id)),
    [exams, selectedIds],
  );

  const openCreate = () => {
    setEditingExam(null);
    setForm({
      title: '',
      description: '',
      course_id: '',
      duration_minutes: 60,
      due_date: '',
      status: 'draft',
      questions: [blankQuestion()],
    });
    setShowDialog(true);
  };

  const openEdit = (exam) => {
    setEditingExam(exam);
    setForm({
      title: exam.title || '',
      description: exam.description || '',
      course_id: exam.course_id || '',
      duration_minutes: exam.duration_minutes || 60,
      due_date: exam.due_date || '',
      status: exam.status || 'draft',
      questions: (exam.questions || []).map(withDefaults),
    });
    setShowDialog(true);
  };

  const addQuestion = () => {
    setForm((prev) => ({
      ...prev,
      questions: [...prev.questions, blankQuestion()],
    }));
  };

  const removeQuestion = (index) => {
    setForm((prev) => ({ ...prev, questions: prev.questions.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const updateQuestion = (index, field, value) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      const options = [...questions[questionIndex].options];
      options[optionIndex] = value;
      questions[questionIndex] = { ...questions[questionIndex], options };
      return { ...prev, questions };
    });
  };

  const changeType = (index, type) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      questions[index] = retypedQuestion(questions[index], type);
      return { ...prev, questions };
    });
  };

  const addOption = (index) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      const options = [...questions[index].options];
      if (options.length >= MAX_OPTIONS) return prev;
      questions[index] = { ...questions[index], options: [...options, ''] };
      return { ...prev, questions };
    });
  };

  const removeOption = (index, optionIndex) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      const question = questions[index];
      if ((question.options || []).length <= MIN_OPTIONS) return prev;
      const options = question.options.filter((_, i) => i !== optionIndex);
      // Keep the answer key pointing at the option it pointed at before.
      let correct = question.correct_answer || 0;
      if (correct === optionIndex) correct = 0;
      else if (correct > optionIndex) correct -= 1;
      questions[index] = { ...question, options, correct_answer: correct };
      return { ...prev, questions };
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.course_id) {
      toast.error('Please select a course');
      return;
    }
    const unanswerable = form.questions.findIndex(
      (question) => question.type === 'short_answer'
        && !(question.accepted_answers || []).some((answer) => String(answer || '').trim()),
    );
    if (unanswerable >= 0) {
      toast.error(`Q${unanswerable + 1} is a short answer with no accepted answer — it could never be marked correct.`);
      return;
    }
    try {
      const total_marks = form.questions.reduce((sum, question) => sum + (Number(question.marks) || 0), 0);
      const payload = { ...form, total_marks };
      if (editingExam) {
        await updateExam(editingExam.id, payload);
        toast.success('Exam updated');
      } else {
        await createExam(payload);
        toast.success('Exam created');
      }
      setShowDialog(false);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save exam');
    }
  };

  const handleInlineStatus = async (exam, status) => {
    try {
      setSavingInline(exam.id);
      const { data } = await updateExam(exam.id, { status });
      setExams((prev) => prev.map((item) => (item.id === exam.id ? data : item)));
      toast.success('Exam status updated');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not update status');
      await loadData();
    } finally {
      setSavingInline('');
    }
  };

  const confirmDelete = (items, type = 'single') => {
    setPendingDelete({ type, exams: items });
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      const ids = pendingDelete.exams.map((exam) => exam.id);
      await Promise.all(ids.map((id) => deleteExam(id)));
      toast.success(`${ids.length} exam${ids.length === 1 ? '' : 's'} deleted`);
      setPendingDelete(null);
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete exams');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      id: 'title',
      header: 'Title',
      sortable: true,
      sortAccessor: (exam) => exam.title || '',
      cellClassName: 'min-w-[240px]',
      cell: (exam) => (
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#062B1A]/5 flex items-center justify-center">
            <ClipboardList className="w-4 h-4 text-[#062B1A]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#062B1A]">{exam.title || 'Untitled exam'}</p>
            <p className="text-xs text-[#062B1A]/45">{exam.duration_minutes || 0} min</p>
          </div>
        </div>
      ),
    },
    {
      id: 'course',
      header: 'Course',
      sortable: true,
      sortAccessor: (exam) => courseLabel(courseMap[exam.course_id]),
      cellClassName: 'min-w-[240px] text-sm text-[#062B1A]/70',
      cell: (exam) => courseLabel(courseMap[exam.course_id]),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cellClassName: 'min-w-[160px]',
      cell: (exam) => (
        <InlineSelect
          value={exam.status || 'draft'}
          disabled={savingInline === exam.id}
          options={publishStatusOptions}
          onChange={(value) => handleInlineStatus(exam, value)}
        />
      ),
    },
    {
      id: 'total_marks',
      header: 'Total marks',
      sortable: true,
      sortAccessor: (exam) => Number(exam.total_marks || 0),
      cellClassName: 'min-w-[130px] text-sm text-[#062B1A]/70',
      cell: (exam) => exam.total_marks || 0,
    },
    {
      id: 'due_date',
      header: 'Due date',
      sortable: true,
      cellClassName: 'min-w-[140px] text-sm text-[#062B1A]/70',
      cell: (exam) => formatDate(exam.due_date),
    },
    {
      id: 'questions',
      header: '# Questions',
      sortable: true,
      sortAccessor: (exam) => exam.questions?.length || 0,
      cellClassName: 'min-w-[120px]',
      cell: (exam) => <StatusBadge value={`${exam.questions?.length || 0}`} />,
    },
  ];

  const rowActions = (exam) => (
    <div className="flex justify-end gap-2">
      <Button type="button" variant="outline" size="sm" className="border-[#062B1A]/15 text-[#062B1A]" onClick={() => openEdit(exam)}>
        <Pencil className="w-4 h-4 mr-2" />
        Edit
      </Button>
      <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => confirmDelete([exam])}>
        <Trash2 className="w-4 h-4 mr-2" />
        Delete
      </Button>
    </div>
  );

  const bulkActions = ({ selectedCount }) => (
    <Button
      type="button"
      size="sm"
      disabled={selectedCount === 0}
      className="bg-red-700 text-white hover:bg-red-800"
      onClick={() => confirmDelete(selectedExams, 'bulk')}
    >
      <Trash2 className="w-4 h-4 mr-2" />
      Bulk Delete
    </Button>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Manage Exams</h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">{exams.length} exam{exams.length === 1 ? '' : 's'} in the current view</p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10] btn-hover" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Exam
        </Button>
      </div>

      <DataTable
        title="Exam Management"
        description="Click a row to edit questions and settings. Status changes save immediately."
        data={filteredExams}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search exams or courses..."
        filters={[
          { id: 'course', value: courseFilter, onChange: setCourseFilter, options: [{ value: 'all', label: 'All courses' }, ...courses.map((course) => ({ value: course.id, label: courseLabel(course) }))] },
          { id: 'status', value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All statuses' }, ...publishStatusOptions] },
        ]}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        bulkActions={bulkActions}
        rowActions={rowActions}
        onRowClick={openEdit}
        emptyMessage="No exams match the current filters."
        pageSize={8}
      />

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>{editingExam ? 'Edit Exam' : 'Create Exam'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Title *</label>
                <Input value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Course *</label>
                <Select value={form.course_id} onValueChange={(value) => setForm((prev) => ({ ...prev, course_id: value }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{courses.map((course) => <SelectItem key={course.id} value={course.id}>{courseLabel(course)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Duration (min)</label>
                <Input type="number" value={form.duration_minutes} onChange={(event) => setForm((prev) => ({ ...prev, duration_minutes: parseInt(event.target.value, 10) || 0 }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Due Date</label>
                <Input type="date" value={form.due_date} onChange={(event) => setForm((prev) => ({ ...prev, due_date: event.target.value }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{publishStatusOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-[#062B1A]">Questions ({form.questions.length})</label>
                <Button size="sm" variant="outline" onClick={addQuestion}>
                  <PlusCircle className="w-3.5 h-3.5 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-4">
                {form.questions.map((question, questionIndex) => (
                  <div key={questionIndex} className="p-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-[#062B1A]/60">Q{questionIndex + 1}</span>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={question.marks} onChange={(event) => updateQuestion(questionIndex, 'marks', parseInt(event.target.value, 10) || 0)} className="w-16 h-7 text-xs border-[rgba(6,43,26,0.12)]" placeholder="Marks" />
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeQuestion(questionIndex)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <Input value={question.text} onChange={(event) => updateQuestion(questionIndex, 'text', event.target.value)} placeholder="Question text" className="mb-2 border-[rgba(6,43,26,0.12)]" />

                    {/* How this question is answered. Everything below follows
                        from it, so it sits above the answer fields. */}
                    <div className="mb-2 flex items-center gap-2">
                      <label className="text-[11px] font-medium text-[#062B1A]/50" htmlFor={`qtype-${questionIndex}`}>
                        Type
                      </label>
                      <Select
                        value={question.type || 'multiple_choice'}
                        onValueChange={(value) => changeType(questionIndex, value)}
                      >
                        <SelectTrigger id={`qtype-${questionIndex}`} className="h-7 w-44 text-xs border-[rgba(6,43,26,0.12)]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {QUESTION_TYPES.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {(question.type || 'multiple_choice') === 'short_answer' ? (
                      <div>
                        <label className="mb-1 block text-[11px] font-medium text-[#062B1A]/50" htmlFor={`qaccept-${questionIndex}`}>
                          Accepted answers
                        </label>
                        <Input
                          id={`qaccept-${questionIndex}`}
                          value={(question.accepted_answers || []).join(', ')}
                          onChange={(event) => updateQuestion(
                            questionIndex,
                            'accepted_answers',
                            event.target.value.split(',').map((answer) => answer.trim()).filter(Boolean),
                          )}
                          placeholder="stakeholder register, stakeholder registry"
                          className="h-8 text-xs border-[rgba(6,43,26,0.12)]"
                        />
                        <p className="mt-1 text-[10px] text-[#062B1A]/40">
                          Comma-separated. Matching ignores capitals and extra spaces.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {question.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`correct-${questionIndex}`}
                                aria-label={`Q${questionIndex + 1} option ${optionIndex + 1} is correct`}
                                checked={question.correct_answer === optionIndex}
                                onChange={() => updateQuestion(questionIndex, 'correct_answer', optionIndex)}
                                className="accent-[#D4AF37]"
                              />
                              <Input
                                value={option}
                                readOnly={question.type === 'true_false'}
                                onChange={(event) => updateOption(questionIndex, optionIndex, event.target.value)}
                                placeholder={`Option ${optionIndex + 1}`}
                                className="h-8 text-xs border-[rgba(6,43,26,0.12)]"
                              />
                              {question.type !== 'true_false' && question.options.length > MIN_OPTIONS && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 shrink-0 text-red-500"
                                  aria-label={`Remove option ${optionIndex + 1} from Q${questionIndex + 1}`}
                                  onClick={() => removeOption(questionIndex, optionIndex)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                        {question.type !== 'true_false' && question.options.length < MAX_OPTIONS && (
                          <Button size="sm" variant="ghost" className="mt-1 h-7 px-2 text-xs" onClick={() => addOption(questionIndex)}>
                            <PlusCircle className="mr-1 h-3 w-3" />Add option
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Shown to the student only after the paper is handed in. */}
                    <div className="mt-2">
                      <label className="mb-1 block text-[11px] font-medium text-[#062B1A]/50" htmlFor={`qexp-${questionIndex}`}>
                        Explanation (optional)
                      </label>
                      <Input
                        id={`qexp-${questionIndex}`}
                        value={question.explanation || ''}
                        onChange={(event) => updateQuestion(questionIndex, 'explanation', event.target.value)}
                        placeholder="Why this answer is right — shown when the student reviews"
                        className="h-8 text-xs border-[rgba(6,43,26,0.12)]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleSave}>{editingExam ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.exams?.length || 0} exam${pendingDelete?.exams?.length === 1 ? '' : 's'}?`}
        description="This permanently removes the selected exam records. Existing student submissions are not cascaded by this action."
        count={pendingDelete?.exams?.length || 0}
        deleting={deleting}
        onConfirm={handleConfirmedDelete}
      />
    </div>
  );
}
